import type { DocMigration } from "../docMigration";

/** Inline snapshot matching built-in trim profile defaults — migration stays isolated from app defaults. */
const builtinTrimProfilesMerged = {
  ["BASE_TRIM_MOLDING_4"]: {
    entityType: "PROFILE",
    id: "BASE_TRIM_MOLDING_4",
    name: '4" Baseboard',
    sortIndex: 0,
    profileType: "CUSTOM",
    polyline: [0, 0, -0.5, 0, -0.5, 4, 0, 4, 0, 0],
  },
  ["BASE_TRIM_MOLDING_6"]: {
    entityType: "PROFILE",
    id: "BASE_TRIM_MOLDING_6",
    name: '6" Baseboard',
    sortIndex: 1,
    profileType: "CUSTOM",
    polyline: [0, 0, -0.5, 0, -0.5, 6, 0, 6, 0, 0],
  },
  ["CROWN_TRIM_MOLDING_1"]: {
    entityType: "PROFILE",
    id: "CROWN_TRIM_MOLDING_1",
    name: "Cove Crown Molding",
    sortIndex: 2,
    profileType: "CUSTOM",
    polyline: [
      0, -5, -1.1, -5, -1.1, -4, -2.1, -4, -2.1, -3, -3.1, -3, -3.1, -2, -4.1, -2, -4.1, -1, -5.1, -1, -5.1, 0, 0, -5,
    ],
  },
  ["CROWN_TRIM_MOLDING_2"]: {
    entityType: "PROFILE",
    id: "CROWN_TRIM_MOLDING_2",
    name: "Simple Crown Molding",
    sortIndex: 3,
    profileType: "CUSTOM",
    polyline: [
      0, 0, 0, -5, -0.2, -5, -0.2, -4, -0.4, -4, -0.4, -3, -0.6, -3, -0.6, -2, -0.8, -2, -0.8, -1, -1, -1, -1, 0, 0, 0,
    ],
  },
  // Matches DEFAULT_PROFILE_DESCRIPTOR in packages/building-types/profiles/defaultBoardTrimProfiles.ts.
  // Id is the literal ProfileExtrusionType.DEFAULT_PROFILE sentinel already stored by existing homes/defaults
  // (e.g. wallRoofBaseBoardProfile, cornerBoardProfile) so that FF-on lookup hits this row instead of
  // silently falling back, with no change in resolved geometry.
  ["DEFAULT_PROFILE"]: {
    entityType: "PROFILE",
    id: "DEFAULT_PROFILE",
    name: "Exterior Transition Board Trim",
    sortIndex: 4,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 0.75,
    height: 7.25,
    nominalWidth: 1,
    nominalHeight: 8,
    elevationNote: '8"',
  },
  // Matches DEFAULT_TRIM_PROFILE_DESCRIPTOR in packages/building-types/profiles/defaultBoardTrimProfiles.ts.
  ["DEFAULT_TRIM_BOARD_1X6"]: {
    entityType: "PROFILE",
    id: "DEFAULT_TRIM_BOARD_1X6",
    name: "Trim Board 1 x 6",
    sortIndex: 5,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 0.75,
    height: 5.5,
    nominalWidth: 1,
    nominalHeight: 6,
    elevationNote: '6"',
  },
  // Matches DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR in packages/building-types/profiles/defaultBoardTrimProfiles.ts.
  ["DEFAULT_CORNER_BOARD_1X4"]: {
    entityType: "PROFILE",
    id: "DEFAULT_CORNER_BOARD_1X4",
    name: "Corner Board 1 x 4",
    sortIndex: 6,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 1,
    height: 4,
    nominalWidth: 1,
    nominalHeight: 4,
    elevationNote: '4"',
  },
  // Matches DEFAULT_DORMER_CORNER_TRIM_PROFILE_DESCRIPTOR in packages/building-types/profiles/defaultBoardTrimProfiles.ts.
  ["DEFAULT_DORMER_CORNER_BOARD_1X2"]: {
    entityType: "PROFILE",
    id: "DEFAULT_DORMER_CORNER_BOARD_1X2",
    name: "Corner Board 1 x 2",
    sortIndex: 7,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 1,
    height: 2,
    nominalWidth: 1,
    nominalHeight: 2,
    elevationNote: '2"',
  },
};

const builtInTrimProfileIds = [
  "BASE_TRIM_MOLDING_4",
  "BASE_TRIM_MOLDING_6",
  "CROWN_TRIM_MOLDING_1",
  "CROWN_TRIM_MOLDING_2",
  "DEFAULT_PROFILE",
  "DEFAULT_TRIM_BOARD_1X6",
  "DEFAULT_CORNER_BOARD_1X4",
  "DEFAULT_DORMER_CORNER_BOARD_1X2",
] as const;

/** Matches docDefault / builtInTrimProfilesGroupId — keep migration isolated from app defaults. */
const builtInTrimProfilesGroupId = "builtInTrimProfilesGroup";

const builtInTrimProfilesGroup = {
  entityType: "GROUP",
  id: builtInTrimProfilesGroupId,
  name: "Built-In Profiles",
  sortIndex: 14,
  isBuiltIn: true,
};

function mergeBuiltinTrimProfiles(json: any): void {
  json.profiles = {
    ...json.profiles,
    ...builtinTrimProfilesMerged,
  };
}

function applyBuiltInTrimProfilesEntityGroups(json: any): void {
  if (json.groups === undefined || json.groups === null || typeof json.groups !== "object") {
    json.groups = {};
  }

  if (json.groups[builtInTrimProfilesGroupId] === undefined) {
    json.groups[builtInTrimProfilesGroupId] = { ...builtInTrimProfilesGroup };
  }

  if (json.entityGroups === undefined || json.entityGroups === null || typeof json.entityGroups !== "object") {
    json.entityGroups = {};
  }

  for (const profileId of builtInTrimProfileIds) {
    const entityGroupKey = `profile.${profileId}`;
    if (json.entityGroups[entityGroupKey] === undefined) {
      json.entityGroups[entityGroupKey] = builtInTrimProfilesGroupId;
    }
  }
}

const migration: DocMigration = {
  id: "20260820_addbuiltintrimprofiles",
  description:
    "Adds the built-in base trim/crown molding profiles and generic default trim/corner board profiles to doc.profiles and groups them under Built-In Profiles (groups + entityGroups)",
  apply(json: any, _warnings: string[], _errors: string[]): any {
    mergeBuiltinTrimProfiles(json);
    applyBuiltInTrimProfilesEntityGroups(json);
    return json;
  },
};

export default migration;
